
    import Component from "vue-class-component";
    import Vue from "vue";
    import { DefaultCatch } from "catch-decorator-ts";
    import { defaultErrorHandler } from "@/common/utils/defaultErrorHandler";
    import type { CutieListAllEntry, Pagination } from "@/app/cuties/list/CutieListApiService";
    import { formatTimeBorn } from "cuties-client-components/src/TextUtils";
    import { Prop, PropSync } from "vue-property-decorator";

    @Component({
        components: {},
    })
    export default class CutieListAllPagination extends Vue {
        @PropSync("pagination", { required: true }) syncedPagination!: Pagination;
        @PropSync("cuties", { required: true }) syncedCuties!: CutieListAllEntry[];
        @Prop({ required: true }) loading!: boolean;
        @Prop({ required: true }) pageSize!: number;
        /** return true if page loaded, false if cancelled */
        @Prop({ required: true }) loadPageOnAction!: () => Promise<boolean>;
        @Prop({ required: true }) tryChangeDirection!: () => boolean;

        private getNextMinId(): number {
            return this.syncedCuties.length === 0 ? 0 :
                this.syncedCuties[0].id + 1;
        }

        private getPreviousMaxId(): number | null {
            return this.syncedCuties.length === 0 ? null :
                this.syncedCuties.slice(-1)[0].id - 1;
        }

        get currentPageId(): string | number | null | undefined {
            const startCutie = this.syncedCuties[0];
            const endCutie = this.syncedCuties.slice(-1)[0];

            if (startCutie && endCutie) {
                // show birth time if page bounds are at least a minute apart
                return Math.abs(startCutie.timeBorn - endCutie.timeBorn) >= 60
                    ? formatTimeBorn(new Date(startCutie.timeBorn * 1000))
                    : startCutie.uid;
            } else {
                return this.syncedPagination.direction === "DESC"
                    ? this.syncedPagination.maxId
                    : this.syncedPagination.minId;
            }
        }

        hasOlderCuties(): boolean {
            if (this.syncedPagination.direction === "DESC") {
                return this.syncedCuties.length === this.pageSize;
            } else {
                return this.syncedPagination.minId > 0;
            }
        }

        hasNewerCuties(): boolean {
            if (this.syncedPagination.direction === "ASC") {
                return this.syncedCuties.length === this.pageSize;
            } else {
                return this.syncedPagination.maxId !== null
                    && this.syncedPagination.maxId !== undefined;
            }
        }

        @DefaultCatch(defaultErrorHandler)
        async loadNewestCuties() {
            this.syncedPagination = {
                direction: "DESC",
                maxId: null,
            };
            await this.loadPageOnAction();
        }

        @DefaultCatch(defaultErrorHandler)
        async loadNewerCuties() {
            this.syncedPagination = {
                direction: "ASC",
                minId: this.getNextMinId(),
            };
            const previousPageCuties = this.syncedCuties;
            await this.loadPageOnAction();
            const remainder = this.pageSize - this.syncedCuties.length;
            if (remainder > 0) {
                // reached the last page, fill remainder with previous page cuties
                if (this.syncedCuties.length === 0) {
                    this.syncedCuties = previousPageCuties;
                } else {
                    this.syncedCuties = [
                        ...this.syncedCuties,
                        ...previousPageCuties.slice(0, remainder)
                            .map(c => ({ ...c, remainderIndicator: !c.remainderIndicator })),
                    ];
                }
                this.tryChangeDirection();
            }
        }

        @DefaultCatch(defaultErrorHandler)
        async loadOlderCuties() {
            this.syncedPagination = {
                direction: "DESC",
                maxId: this.getPreviousMaxId(),
            };
            const previousPageCuties = this.syncedCuties;
            await this.loadPageOnAction();
            const remainder = this.pageSize - this.syncedCuties.length;
            if (remainder > 0) {
                if (this.syncedCuties.length === 0) {
                    this.syncedCuties = previousPageCuties;
                } else {
                    this.syncedCuties = [
                        ...previousPageCuties.slice(-remainder)
                            .map(c => ({ ...c, remainderIndicator: !c.remainderIndicator })),
                        ...this.syncedCuties,
                    ];
                }
                this.tryChangeDirection();
            }
        }

        @DefaultCatch(defaultErrorHandler)
        async loadOldestCuties() {
            this.syncedPagination = {
                direction: "ASC",
                minId: 0,
            };
            await this.loadPageOnAction();
        }
    }
